import React, { useState } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered, Quote, Link2, Unlink, ImagePlus, Minus, Undo2, Redo2, Check, X } from "lucide-react";
import { FileUpload } from "@/components/FileUpload";
import { Input } from "@/components/ui/input";

const Tool = ({ active, onClick, disabled, title, testId, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title} aria-pressed={active}
    className={`h-8 w-8 grid place-items-center rounded-lg transition-colors disabled:opacity-40 ${active ? "text-white" : "hover:bg-black/5"}`}
    style={active ? { background: "var(--yalola-primary)" } : { color: "var(--yalola-text)" }} data-testid={testId}>{children}</button>
);

// Éditeur riche (Tiptap) pour le corps des articles : titres H2/H3, gras, italique, souligné, listes, citation, lien, image, séparateur.
// `value` initialise l'éditeur : remonter avec `key` pour changer de contenu (ex. onglet de langue).
export const RichTextEditor = ({ value, onChange, placeholder = "Rédige ton article…", testId = "rte" }) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, defaultProtocol: "https" }, codeBlock: false, code: false }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.isEmpty ? "" : ed.getHTML()),
    editorProps: { attributes: { class: "yalola-article rte-content min-h-[320px] px-4 py-3 outline-none", "data-testid": `${testId}-content` } },
  });
  const st = useEditorState({
    editor,
    selector: ({ editor: ed }) => ed ? {
      bold: ed.isActive("bold"), italic: ed.isActive("italic"), underline: ed.isActive("underline"), h2: ed.isActive("heading", { level: 2 }), h3: ed.isActive("heading", { level: 3 }),
      ul: ed.isActive("bulletList"), ol: ed.isActive("orderedList"), quote: ed.isActive("blockquote"), link: ed.isActive("link"), href: ed.getAttributes("link").href || "",
      undo: ed.can().undo(), redo: ed.can().redo(),
    } : {},
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const openLink = () => { setLinkUrl(st.href || ""); setLinkOpen(true); };
  const applyLink = () => {
    const href = linkUrl.trim();
    if (href) c().extendMarkRange("link").setLink({ href: /^(https?:\/\/|mailto:|\/)/i.test(href) ? href : `https://${href}`, target: "_blank" }).run();
    else c().extendMarkRange("link").unsetLink().run();
    setLinkOpen(false);
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface, #fff)" }} data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`${testId}-toolbar`}>
        <Tool active={st.h2} onClick={() => c().toggleHeading({ level: 2 }).run()} title="Titre" testId={`${testId}-h2`}><Heading2 size={16} /></Tool>
        <Tool active={st.h3} onClick={() => c().toggleHeading({ level: 3 }).run()} title="Sous-titre" testId={`${testId}-h3`}><Heading3 size={16} /></Tool>
        <span className="w-px h-5 mx-1" style={{ background: "var(--yalola-border)" }} />
        <Tool active={st.bold} onClick={() => c().toggleBold().run()} title="Gras" testId={`${testId}-bold`}><Bold size={16} /></Tool>
        <Tool active={st.italic} onClick={() => c().toggleItalic().run()} title="Italique" testId={`${testId}-italic`}><Italic size={16} /></Tool>
        <Tool active={st.underline} onClick={() => c().toggleUnderline().run()} title="Souligné" testId={`${testId}-underline`}><UnderlineIcon size={16} /></Tool>
        <span className="w-px h-5 mx-1" style={{ background: "var(--yalola-border)" }} />
        <Tool active={st.ul} onClick={() => c().toggleBulletList().run()} title="Liste à puces" testId={`${testId}-ul`}><List size={16} /></Tool>
        <Tool active={st.ol} onClick={() => c().toggleOrderedList().run()} title="Liste numérotée" testId={`${testId}-ol`}><ListOrdered size={16} /></Tool>
        <Tool active={st.quote} onClick={() => c().toggleBlockquote().run()} title="Citation" testId={`${testId}-quote`}><Quote size={16} /></Tool>
        <Tool onClick={() => c().setHorizontalRule().run()} title="Séparateur" testId={`${testId}-hr`}><Minus size={16} /></Tool>
        <span className="w-px h-5 mx-1" style={{ background: "var(--yalola-border)" }} />
        <Tool active={st.link} onClick={openLink} title="Lien" testId={`${testId}-link`}><Link2 size={16} /></Tool>
        {st.link && <Tool onClick={() => c().extendMarkRange("link").unsetLink().run()} title="Retirer le lien" testId={`${testId}-unlink`}><Unlink size={16} /></Tool>}
        <FileUpload accept="image/*" variant="custom" context="feed" testId={`${testId}-image`} className="relative h-8 w-8 grid place-items-center rounded-lg hover:bg-black/5"
          onUploaded={(url) => c().setImage({ src: url, alt: "" }).run()}><ImagePlus size={16} style={{ color: "var(--yalola-text)" }} /></FileUpload>
        <span className="ml-auto flex items-center gap-0.5">
          <Tool disabled={!st.undo} onClick={() => c().undo().run()} title="Annuler" testId={`${testId}-undo`}><Undo2 size={16} /></Tool>
          <Tool disabled={!st.redo} onClick={() => c().redo().run()} title="Rétablir" testId={`${testId}-redo`}><Redo2 size={16} /></Tool>
        </span>
      </div>
      {linkOpen && (
        <div className="flex items-center gap-2 border-b px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`${testId}-link-row`}>
          <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://… (vide = retirer le lien)" className="h-8 text-sm" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } if (e.key === "Escape") setLinkOpen(false); }} data-testid={`${testId}-link-input`} />
          <Tool onClick={applyLink} title="Appliquer" testId={`${testId}-link-apply`}><Check size={16} /></Tool>
          <Tool onClick={() => setLinkOpen(false)} title="Fermer" testId={`${testId}-link-cancel`}><X size={16} /></Tool>
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
};
